import { useMemo, useState } from 'react';
import { RichText } from '../../richText/RichText';
import { Link } from '../../router/Link';
import {
  Button,
  ConfirmButton,
  Field,
  LoadingStatus,
  Menu,
  Modal,
  Notice,
  Panel,
  ProblemPanel,
  Spinner,
  StatePanel,
  Thumbnail,
  buttonClass,
  cx,
} from '../../ui';
import layout from '../../ui/layout.module.css';
import { AddToPlaylist } from '../../playlists/AddToPlaylist';
import { CreatePlaylistForm } from '../../playlists/CreatePlaylistForm';
import { WatchLaterButton } from '../../playlists/WatchLaterButton';
import { AddToQueue } from '../../queue/AddToQueue';
import { QueueCardAsk } from '../../queue/QueueCardAsk';
import { useQueueCardAsk } from '../../queue/useQueueCardAsk';
import { useQueueCardPress } from '../../queue/useQueueCardPress';
import { useDeArrowBranding } from '../../dearrow/useDeArrowBranding';
import { CardActions } from '../../listing/CardActions';
import { FeedGridSkeleton } from '../../listing/FeedGrid';
import { ThumbnailOverlay } from '../../listing/ThumbnailOverlay';
import { useAutoLoadMore } from '../../listing/useAutoLoadMore';
import { useGridPreview } from '../../listing/useGridPreview';
import { privacyDetail, privacyLabel } from '../../playlists/model';
import { useCopy } from '../../strings/useCopy';
import {
  usePlaylist,
  type PlaylistBinding,
  type PlaylistEditBinding,
  type PlaylistManageBinding,
  type VideoFindState,
} from './usePlaylist';
import { playlistOrderMenu, playlistQueueCandidate } from './model';
import type { PlaylistEntryRow, PlaylistHeader } from './model';
import styles from './playlist.module.css';
import { GridSearch } from '../../listing/GridSearch';
import { shiftRangePress } from '../../listing/selectPress';
import { useGridFocus } from '../../listing/useGridFocus';

const PRIVACIES = ['public', 'unlisted', 'private'] as const;

/**
 * §8's playlist page. Renders props (R13) — every string arrived formatted, every href arrived built,
 * and the only branch is on `state.kind`.
 *
 * A `<div>` rather than a `<main>`: the shell owns that landmark.
 */
export function PlaylistView({ playlistId }: { readonly playlistId: string }) {
  const binding = usePlaylist(playlistId);
  const state = binding.state;
  const copy = useCopy().text;
  const words = copy.playlist.page;

  return (
    <div className={layout.wide}>
      <section className={styles.playlist}>
        {/*
         * 2d's lesson applied before it could bite again: the header is drawn only when there is one.
         * A failed channel page used to render an empty avatar ring, an empty tab strip and a live sort
         * menu above "No such channel" — chrome for a page that is not there.
         */}
        {state.kind === 'loading' ? <PendingHead playlistId={playlistId} /> : null}
        {state.kind === 'problem' ? (
          <>
            <FailedHead playlistId={playlistId} />
            <ProblemPanel copy={state.copy} onRetry={binding.retry} />
          </>
        ) : null}

        {state.kind === 'empty' ? (
          <>
            {/* The header comes first even here: an empty playlist that is *ours* is the normal state
                right after creating one, and it still has a title, a visibility and controls. The
                manage panel renders too — an empty playlist is exactly where "Add videos" matters. */}
            <HeadRow header={state.header} binding={binding} />
            {state.header.canEdit && binding.manage.active ? (
              <ManagePanel manage={binding.manage} />
            ) : null}
            {/* Centred, with air above — where the grid would be, not hugging the header. */}
            <StatePanel title={state.title} detail={state.detail} />
          </>
        ) : null}

        {state.kind === 'loading' ? (
          <>
            <LoadingStatus>{words.loading}</LoadingStatus>
            <FeedGridSkeleton cards={10} />
          </>
        ) : null}

        {state.kind === 'ready' ? (
          <>
            <HeadRow header={state.header} binding={binding} />
            {binding.entryProblem === null ? null : (
              <Notice tone="caution" live>
                <strong>{binding.entryProblem.title}.</strong> {binding.entryProblem.detail}
              </Notice>
            )}
            {state.header.canEdit && binding.manage.active ? (
              <ManagePanel manage={binding.manage} />
            ) : null}
            <ControlsRow
              binding={binding}
              manageBar={state.header.canEdit && binding.manage.active}
              withOrder={state.loaded > 1}
            />
            {state.nothingMatches ? (
              <p className={styles.noMatches}>
                {copy.listing.search.noMatches(binding.search.trim())}
              </p>
            ) : (
              <EntryGrid
                rows={state.rows}
                playlistId={playlistId}
                manage={state.header.canEdit && binding.manage.active}
                onToggle={binding.manage.toggleSelect}
                remove={
                  state.header.canEdit && !binding.manage.active
                    ? { act: binding.removeEntry, busy: binding.removingIndexIds }
                    : null
                }
              />
            )}
            <Paging binding={binding} state={state} />
          </>
        ) : null}
      </section>
    </div>
  );
}

/**
 * The id while the first page is in flight. It is what the reader clicked, and a heading that fills in
 * a moment later shifts the whole page — 2d's `ChannelHead` argument, and the same fix.
 */
function PendingHead({ playlistId }: { readonly playlistId: string }) {
  return (
    <header className={styles.head}>
      <h1 className={styles.title}>
        <span className={styles.pending}>{playlistId}</span>
      </h1>
    </header>
  );
}

function FailedHead({ playlistId }: { readonly playlistId: string }) {
  return (
    <header className={styles.head}>
      <h1 className={styles.title}>
        <span className={styles.pending}>{playlistId}</span>
      </h1>
    </header>
  );
}

/**
 * The header with the page's actions on its top right: browsing, Play all keeps its accent
 * beside Manage; managing, Edit takes its place beside Done — each mode shows only the acts that
 * belong to it (playing all is not managing, and editing the head is). Play all is a `<Link>`, not
 * a button — it is a destination, and it carries `list=` and `index=` so the watch page knows which
 * playlist opened it; `buttonClass` dresses it, the same way the feed's Manage link is dressed.
 */
function HeadRow({ header, binding }: { readonly header: PlaylistHeader; readonly binding: PlaylistBinding }) {
  const words = useCopy().text.playlist;
  const managing = header.canEdit && binding.manage.active;

  return (
    <div className={styles.headRow}>
      <Head header={header} refreshing={binding.refreshing} />
      <div className={styles.headActions}>
        {managing || header.playAllHref === null ? null : (
          <Link className={buttonClass({ variant: 'primary', size: 'sm' })} href={header.playAllHref}>
            {words.page.playAll}
          </Link>
        )}
        {managing ? (
          <Button variant="default" size="sm" onClick={() => binding.edit.setOpen(true)}>
            {words.page.edit}
          </Button>
        ) : null}
        {header.canEdit ? (
          <Button
            variant="default"
            size="sm"
            onClick={binding.manage.toggle}
            ariaPressed={binding.manage.active}
          >
            {binding.manage.active ? words.manage.leave : words.manage.enter}
          </Button>
        ) : null}
      </div>
      {header.canEdit ? <EditModal header={header} edit={binding.edit} /> : null}
    </div>
  );
}

/**
 * The controls line above the grid (CLAUDE.md): the bulk acts (managing) on the left, the
 * search and the display-order menu on the right — one line, so entering manage mode adds no second
 * row. The order menu is client-side by force — the API has no reorder endpoint (§7i) — and
 * remembered per playlist in this browser (`playlists/orderMemory.ts`), which is why it works on
 * mirrored playlists too, not only your own.
 */
function ControlsRow({
  binding,
  manageBar,
  withOrder,
}: {
  readonly binding: PlaylistBinding;
  readonly manageBar: boolean;
  readonly withOrder: boolean;
}) {
  const [open, setOpen] = useState(false);
  const searchWords = useCopy().text.listing.search;

  return (
    <div className={styles.controls}>
      {manageBar ? <ManageBar manage={binding.manage} /> : null}
      <GridSearch
        id="search-playlist"
        value={binding.search}
        onChange={binding.setSearch}
        words={searchWords}
        className={styles.controlsSearch}
      />
      {withOrder ? (
        <Menu
          menu={playlistOrderMenu(binding.reversed)}
          open={open}
          onOpenChange={setOpen}
          onSelect={(itemId) => {
            setOpen(false);
            binding.setReversed(itemId === 'reversed');
          }}
        />
      ) : null}
    </div>
  );
}

function Head({
  header,
  refreshing,
}: {
  readonly header: PlaylistHeader;
  readonly refreshing: boolean;
}) {
  const words = useCopy().text.playlist.page;

  return (
    <header className={header.thumbnailUrl === null ? styles.headNoCover : styles.head}>
      {/*
       * Omitted entirely when the record carried no art, rather than drawn as an empty frame — and the
       * two cases are genuinely different. `Thumbnail` draws its blank frame when a *URL* fails to
       * load, which is right (2 of 40 `/vi/` images fail on first request, and the frame stops the
       * layout jumping). But an Invidious-native playlist has `playlistThumbnail: null` structurally,
       * so the frame would be 352 × 198 of permanent nothing at the top of every one of them.
       */}
      {header.thumbnailUrl === null ? null : (
        <div className={styles.cover}>
          {/* The 16:9 box owns the ratio: the declared dimensions are the size *requested*, not
              the size served. */}
          <Thumbnail src={header.thumbnailUrl} className={styles.coverImage} />
        </div>
      )}

      <div className={styles.headText}>
        <h1 className={styles.title}>
          {header.title}
          {refreshing ? (
            <span className={styles.refreshing}>
              <Spinner />
            </span>
          ) : null}
        </h1>

        <p className={styles.byline}>
          {header.avatarUrl === null ? null : (
            <img className={styles.avatar} src={header.avatarUrl} alt="" decoding="async" />
          )}
          {header.author === '' ? null : header.authorHref === null ? (
            // No link on purpose: an Invidious-native playlist carries no channel at all, and a
            // mirrored one can carry `authorUrl: "/channel/"` — a URL that routes nowhere.
            <span className={styles.author}>{header.author}</span>
          ) : (
            <Link className={styles.author} href={header.authorHref}>
              {header.author}
            </Link>
          )}
        </p>

        {/* Each figure is independently optional: a fresh Invidious-native playlist genuinely has
            `viewCount: 0`, and a zero printed as a figure is a claim (2d's About tab). */}
        <p className={styles.meta}>
          {header.videoCountText === null ? null : <span>{header.videoCountText}</span>}
          {header.viewCountText === null ? null : (
            <span title={header.viewCountTitle ?? undefined}>
              {words.views(header.viewCountText)}
            </span>
          )}
          {header.updatedText === null ? null : (
            <span title={header.updatedTitle ?? undefined}>{words.updated(header.updatedText)}</span>
          )}
          {/*
           * Two states rather than three, and the caveat says why. `isListed` is a boolean over the
           * three privacy settings and no read carries `privacy`, so "Not public" is the strongest
           * true statement about a playlist this session did not just write.
           */}
          <span
            className={header.visibility.caveat === null ? styles.badge : styles.badgeInexact}
            title={header.visibility.title}
          >
            {header.visibility.label}
          </span>
          {header.invidiousOwned ? (
            <span className={styles.badge}>{words.onThisInstance}</span>
          ) : null}
        </p>

        {header.hasDescription ? (
          <RichText className={styles.description} nodes={header.descriptionNodes} />
        ) : null}
      </div>
    </header>
  );
}

/**
 * Renaming, describing, visibility and deletion — one dialog, out of the header,
 * because they are one `PATCH`.
 *
 * That is not a layout choice: the endpoint **requires `title` on every call**, so "change the
 * description" is already a call that carries the title. A page with three separate controls would be
 * three ways to send the same request, each able to write a stale title over a fresh one.
 */
function EditModal({
  header,
  edit,
}: {
  readonly header: PlaylistHeader;
  readonly edit: PlaylistEditBinding;
}) {
  const copy = useCopy().text;
  const words = copy.playlist.editor;
  const detail = privacyDetail();

  return (
    <Modal
      open={edit.open}
      onClose={() => edit.setOpen(false)}
      title={words.sectionLabel}
      closeLabel={copy.playlist.page.close}
    >
      <div className={styles.edit}>
      <Field
        id="playlist-title"
        label={words.titleLabel}
        value={edit.title}
        onChange={edit.setTitle}
        problem={edit.title.trim() === '' ? copy.playlists.title.missing : null}
      />
      <Field
        id="playlist-description"
        label={words.descriptionLabel}
        value={edit.description}
        onChange={edit.setDescription}
        multiline={4}
        placeholder={words.descriptionPlaceholder}
      />

      <div className={styles.editPrivacy}>
        <span className={styles.editLabel} id="playlist-privacy">
          {words.privacyLabel}
        </span>
        <div className={styles.privacyGroup} role="radiogroup" aria-labelledby="playlist-privacy">
          {PRIVACIES.map((privacy) => (
            <button
              key={privacy}
              type="button"
              role="radio"
              aria-checked={edit.privacy === privacy}
              className={styles.privacyOption}
              title={detail[privacy]}
              onClick={() => edit.setPrivacy(privacy)}
            >
              {privacyLabel()[privacy]}
            </button>
          ))}
        </div>
        <p className={styles.editHelp}>
          {edit.privacy === null
            ? // Nothing is preselected, and that is honest rather than lazy: the API does not report
              // which of unlisted and private a playlist is, so a highlighted option would be a guess.
              words.currently(header.visibility.label.toLowerCase(), header.visibility.caveat ?? '')
            : detail[edit.privacy]}
        </p>
      </div>

      <div className={styles.editActions}>
        <Button variant="primary" onClick={edit.save} disabled={edit.saving || edit.title.trim() === ''}>
          {edit.saving ? words.saving : words.save}
        </Button>
        {edit.saved ? (
          <span className={styles.saved} role="status">
            {words.saved}
          </span>
        ) : null}
        <span className={styles.editSpacer} />
        {/* Still two clicks — deletion has no undo here, no trash and no restore endpoint — but the
            question moved into the button itself (ConfirmButton), the app's one deletion grammar. */}
        <ConfirmButton
          variant="subtle"
          confirmLabel={copy.shell.confirm}
          confirmTitle={words.confirmDelete}
          disabled={edit.deleting}
          onConfirm={edit.deletePlaylist}
        >
          {edit.deleting ? words.deleting : words.delete}
        </ConfirmButton>
      </div>

      {header.reorderSupported ? null : (
        <p className={styles.editNote}>{words.noReorder}</p>
      )}

      {edit.problem === null ? null : (
        <Notice tone="caution" live>
          <strong>{edit.problem.title}.</strong> {edit.problem.detail}
        </Notice>
      )}
      </div>
    </Modal>
  );
}

/* --------------------------------------------------------------------------------------------------
 * Manage mode — the add box, the bulk bar, and the transfer dialog
 * ------------------------------------------------------------------------------------------------ */

function ManagePanel({ manage }: { readonly manage: PlaylistManageBinding }) {
  const words = useCopy().text.playlist.manage;

  return (
    <>
      <Panel as="section" ariaLabel={words.add.heading} className={styles.add}>
        <h2 className={styles.addHeading}>{words.add.heading}</h2>
        <div className={styles.addRow}>
          <Field
            id="playlist-add-video"
            label={words.add.label}
            hideLabel
            type="search"
            value={manage.addInput}
            onChange={manage.setAddInput}
            onSubmit={manage.find}
            placeholder={words.add.placeholder}
            clear={{ label: words.add.clear }}
            className={styles.addField}
          />
          <Button variant="primary" onClick={manage.find} disabled={manage.findState.kind === 'busy'}>
            {words.add.submit}
          </Button>
        </div>
        <VideoCandidates
          state={manage.findState}
          addStates={manage.addStates}
          onAdd={manage.addVideo}
        />
      </Panel>

      {manage.problem === null ? null : (
        <Notice tone="caution" live>
          <strong>{manage.problem.title}.</strong> {manage.problem.detail}
        </Notice>
      )}

      <TransferModal manage={manage} />
    </>
  );
}

/**
 * The three acts over the selection — the Playlists page's bar, sharing the controls line with the
 * search and the order menu. Removal confirms in place: first click arms it red, the
 * second one acts.
 */
function ManageBar({ manage }: { readonly manage: PlaylistManageBinding }) {
  const copy = useCopy().text;
  const words = copy.playlist.manage;
  const busy = manage.working !== null;

  return (
    <div className={styles.manageBar}>
      <ConfirmButton
        variant="default"
        size="sm"
        disabled={manage.selectedCount === 0 || busy}
        confirmLabel={copy.shell.confirm}
        onConfirm={manage.removeSelected}
      >
        {words.removeSelected}
      </ConfirmButton>
      <span className={styles.manageDivider} aria-hidden="true" />
      <Button
        variant="default"
        size="sm"
        disabled={manage.selectedCount === 0 || busy}
        onClick={() => manage.openTransfer('copy')}
      >
        {words.copySelected}
      </Button>
      <Button
        variant="default"
        size="sm"
        disabled={manage.selectedCount === 0 || busy}
        onClick={() => manage.openTransfer('move')}
      >
        {words.moveSelected}
      </Button>
      {/* The selection's size lives here, not in the buttons — always rendered, so the bar
          holds its shape when the first card is ticked and the zero explains the disabled row. */}
      <span className={styles.selectedCount}>{words.selectedCount(manage.selectedCount)}</span>
      {/* The way out of a selection that isn't a deselection per card — ghost, because it
          changes nothing on the instance. */}
      <Button
        variant="ghost"
        size="sm"
        disabled={manage.selectedCount === 0 || busy}
        onClick={manage.clearSelection}
      >
        {words.clearSelection}
      </Button>
      {manage.working === null ? null : (
        <span className={styles.working} role="status">
          {words.working(manage.working.done, manage.working.total)}
        </span>
      )}
    </div>
  );
}

function VideoCandidates({
  state,
  addStates,
  onAdd,
}: {
  readonly state: VideoFindState;
  readonly addStates: ReadonlyMap<string, 'adding' | 'added' | 'failed'>;
  readonly onAdd: (videoId: string) => void;
}) {
  const words = useCopy().text.playlist.manage.add;

  switch (state.kind) {
    case 'idle':
      return null;
    case 'busy':
      return (
        <div className={styles.finding} role="status">
          <Spinner />
          <span>{words.searching}</span>
        </div>
      );
    case 'nothing':
      return <p className={styles.findNote}>{words.nothing(state.q)}</p>;
    case 'not-available':
      return <p className={styles.findNote}>{words.notAvailable}</p>;
    case 'problem':
      return (
        <Notice tone="caution" live>
          {state.detail}
        </Notice>
      );
    case 'results':
      return (
        <ul className={styles.candidates}>
          {state.rows.map((row) => {
            const added = addStates.get(row.videoId);
            return (
              <li className={styles.candidate} key={row.videoId}>
                <span className={styles.candidateThumb}>
                  <Thumbnail
                    src={row.thumbnailUrl}
                    badge={
                      row.durationText === null
                        ? null
                        : { kind: 'duration', text: row.durationText, title: null }
                    }
                  />
                </span>
                <span className={styles.candidateText}>
                  <span className={styles.candidateTitle}>{row.title}</span>
                  {row.author === null ? null : (
                    <span className={styles.candidateMeta}>{row.author}</span>
                  )}
                </span>
                <span className={styles.candidateAction} aria-live="polite">
                  {added === 'added' ? <span className={styles.addedWord}>{words.added}</span> : null}
                  {added === 'failed' ? (
                    <span className={styles.failedWord}>{words.failed}</span>
                  ) : null}
                  <Button
                    size="sm"
                    disabled={added === 'adding' || added === 'added'}
                    onClick={() => onAdd(row.videoId)}
                  >
                    {added === 'adding' ? <Spinner /> : words.action}
                  </Button>
                </span>
              </li>
            );
          })}
        </ul>
      );
  }
}

/**
 * The copy/move target dialog: every owned playlist but this one, and the shared create form under
 * them — creating a target runs the same transfer the row click does.
 */
function TransferModal({ manage }: { readonly manage: PlaylistManageBinding }) {
  const manageWords = useCopy().text.playlist.manage;
  const words = manageWords.transfer;
  const busy = manage.working !== null;

  return (
    <Modal
      open={manage.transfer !== null}
      onClose={manage.closeTransfer}
      title={manage.transfer === 'move' ? words.moveTitle : words.copyTitle}
      closeLabel={words.close}
    >
      <div className={styles.transferContent}>
        {manage.targets === null && manage.targetsProblem === null ? (
          <div className={styles.finding} role="status">
            <Spinner />
            <span>{words.loading}</span>
          </div>
        ) : null}

        {manage.targetsProblem === null ? null : (
          <Notice tone="caution">
            <strong>{manage.targetsProblem.title}.</strong> {manage.targetsProblem.detail}
          </Notice>
        )}

        {manage.targets !== null && manage.targets.length === 0 ? (
          <p className={styles.findNote}>{words.none}</p>
        ) : null}

        {manage.targets === null || manage.targets.length === 0 ? null : (
          <ul className={styles.targets}>
            {manage.targets.map((target) => (
              <li key={target.playlistId}>
                <button
                  type="button"
                  className={styles.targetButton}
                  disabled={busy}
                  onClick={() => manage.transferTo(target.playlistId)}
                >
                  <span className={styles.targetTitle}>{target.title}</span>
                  {target.countText === null ? null : (
                    <span className={styles.targetMeta}>{target.countText}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {manage.working === null ? null : (
          <p className={styles.working} role="status">
            {manageWords.working(manage.working.done, manage.working.total)}
          </p>
        )}

        <CreatePlaylistForm
          id="playlist-transfer-create"
          submitLabel={manage.transfer === 'move' ? words.moveCreate : words.copyCreate}
          onCreated={manage.transferTo}
        />
      </div>
    </Modal>
  );
}

/* --------------------------------------------------------------------------------------------------
 * The entry grid
 * ------------------------------------------------------------------------------------------------ */

/**
 * The entries as feed-shaped cards, with the position riding each thumbnail's corner.
 *
 * `useGridFocus` still applies — the grid measures its own column count, so §9's roving focus gives
 * the whole list one tab stop and `focusMoveFor` the same answers a feed gets. In manage mode the
 * roving `tabIndex` moves to the checkbox, which is the card's one control once a selection exists.
 */
/** The browse-mode per-entry removal — `null` while managing (the bulk bar owns removal there). */
interface EntryRemove {
  readonly act: (indexId: string) => void;
  readonly busy: ReadonlySet<string>;
}

function EntryGrid({
  rows,
  playlistId,
  manage,
  onToggle,
  remove,
}: {
  readonly rows: readonly PlaylistEntryRow[];
  readonly playlistId: string;
  readonly manage: boolean;
  readonly onToggle: (indexId: string, range: boolean) => void;
  readonly remove: EntryRemove | null;
}) {
  const { attachGrid, onKeyDown, tabIndexFor, onFocusIndex } = useGridFocus(rows.length);
  const words = useCopy().text.playlist.page;

  return (
    <ol className={styles.grid} ref={attachGrid} onKeyDown={onKeyDown} aria-label={words.entriesLabel}>
      {rows.map((row, index) => (
        <EntryCard
          key={row.key}
          row={row}
          playlistId={playlistId}
          manage={manage}
          onToggle={onToggle}
          remove={remove}
          tabIndex={tabIndexFor(index)}
          onFocus={() => onFocusIndex(index)}
        />
      ))}
    </ol>
  );
}

/**
 * One entry. Its click semantics: in manage mode the whole card is its checkbox's `<label>` from
 * the first click — a click always toggles the selection and never navigates, so entering the mode
 * *is* the statement of intent (the earlier shape, link-until-something-is-selected, lost the first
 * card of every session to a mis-click). Nested interactive elements are invalid HTML anyway, so
 * label-or-link was always the choice.
 */
function EntryCard({
  row,
  playlistId,
  manage,
  onToggle,
  remove,
  tabIndex,
  onFocus,
}: {
  readonly row: PlaylistEntryRow;
  readonly playlistId: string;
  readonly manage: boolean;
  readonly onToggle: (indexId: string, range: boolean) => void;
  readonly remove: EntryRemove | null;
  readonly tabIndex: number;
  readonly onFocus: () => void;
}) {
  const copy = useCopy().text;
  const words = copy.playlist.page;
  const confirmWord = copy.shell.confirm;
  const gone = row.kind === 'unavailable';
  // DeArrow (2026-08-02): a dead entry has no video behind it, so nothing is looked up.
  const dearrow = useDeArrowBranding(gone ? null : row.videoId);
  const entryTitle = row.kind === 'video' ? (dearrow.title ?? row.title) : null;
  // The feed grid's hover preview here too (2026-08-02) — it only ever ran on `ListingCard`,
  // so this grid and the history one had thumbnails that never moved. A dead entry has no video
  // behind it, and a premiere has not been recorded, so neither arms.
  const preview = useGridPreview({
    videoId: row.kind === 'video' ? row.videoId : '',
    durationSec: row.kind === 'video' ? row.durationSec : null,
    noPreview: row.kind !== 'video' || row.badge?.kind === 'upcoming',
  });
  /*
   * The queue's view of this entry, and what a press on it means while a list is running
   * (`queue/useQueueCardPress.ts`). `null` for a dead entry, which has no video behind it — the same
   * line Watch later and Save to playlist already draw on this card.
   */
  const queueItem = useMemo(
    () => (row.kind === 'video' ? playlistQueueCandidate(row, entryTitle ?? row.title) : null),
    [entryTitle, row],
  );
  const onPress = useQueueCardPress(queueItem);
  const asking = useQueueCardAsk(queueItem);

  const select =
    manage && row.indexId !== null ? (
      <input
        type="checkbox"
        className={styles.select}
        checked={row.selected}
        // The roving `tabIndex` always: a checkbox per card at natural tab order would be one tab
        // stop per entry, which on a 200-entry playlist is the list `useGridFocus` exists to avoid.
        tabIndex={tabIndex}
        /* The modifier for a press that landed **on the box itself**, where the control's own
           `change` fires in both engines and carries it. Everywhere else on the card the label's
           click answers a shifted press instead — no engine forwards that one dependably
           (`listing/selectPress.ts`, 2026-08-02). */
        onChange={(event) => {
          onToggle(
            row.indexId ?? '',
            event.nativeEvent instanceof MouseEvent && event.nativeEvent.shiftKey,
          );
        }}
        aria-label={row.selectLabel}
      />
    ) : null;

  /*
   * The corner controls, over the frame where the manage checkbox otherwise sits — the two never
   * coexist, which is what the `manage` arm below is for. `listing/CardActions.tsx` is the shared
   * wrapper (2026-08-03), which is also what gave this page's copy the touch-screen arm it never
   * had; `surface` because these are ghost buttons rather than the feed grids' `media` ones.
   *
   * The stack is **not** gated on the remove any more (2026-08-07). It used to be — the whole span
   * was the remove's own — so a playlist the reader cannot edit had no corner controls at all, which
   * on a mirrored YouTube list or somebody else's playlist meant no Watch later, no Save to playlist
   * and nowhere to put the queue button a queue does not need an account for.
   */
  const removing = row.indexId !== null && remove !== null && remove.busy.has(row.indexId);
  const canRemove = remove !== null && row.indexId !== null;
  const cornerActions =
    manage || (!canRemove && queueItem === null) ? null : (
      <CardActions backdrop="surface" covered={asking}>
        {/* First click arms it — red, wearing the confirm word — second click removes. Dead entries
            keep theirs: a video YouTube no longer serves is what a reader wants out of the list. */}
        {canRemove ? (
          <ConfirmButton
            size="sm"
            variant="ghost"
            icon
            tabIndex={tabIndex}
            label={removing ? words.removing : words.remove(entryTitle ?? words.entryGone)}
            /* The short act as the hover word; the accessible name keeps the title, and the
               armed `confirmTitle` names the video the second click destroys. */
            title={removing ? words.removing : words.removeTitle}
            confirmLabel={confirmWord}
            confirmTitle={words.remove(entryTitle ?? words.entryGone)}
            disabled={removing}
            onConfirm={() => remove?.act(row.indexId ?? '')}
          >
            {removing ? <Spinner /> : <RemoveGlyph />}
          </ConfirmButton>
        ) : null}
        {/*
         * Under the remove where there is one: Watch later, then Save to playlist, then the queue.
         * Watch later is suppressed on the appointed list itself, since the remove above it already
         * *is* "remove from Watch later". A dead entry gets none of the three — the add endpoint
         * answers `404 Video unavailable`, and a queue row with no video behind it is a hole.
         */}
        {row.kind === 'video' && queueItem !== null ? (
          <>
            <WatchLaterButton
              face="corner"
              cornerVariant="ghost"
              videoId={row.videoId}
              videoTitle={entryTitle ?? row.title}
              tabIndex={tabIndex}
              suppressForPlaylistId={playlistId}
            />
            <AddToPlaylist
              face="corner"
              cornerVariant="ghost"
              videoId={row.videoId}
              videoTitle={entryTitle ?? row.title}
              tabIndex={tabIndex}
            />
            {/* The only one of the three that renders signed out: a queue belongs to this browser
                tab, so there is no account for it to need (`queue/AddToQueue.tsx`). */}
            <AddToQueue item={queueItem} tabIndex={tabIndex} />
          </>
        ) : null}
      </CardActions>
    );

  const frame = (
    <span className={cx(styles.frame, gone && styles.goneFrame)}>
      {row.kind === 'video' ? (
        <Thumbnail
          src={dearrow.thumbnailUrl ?? row.thumbnailUrl}
          badge={row.badge}
          progress={preview.progress ?? row.progress}
          onPointerEnter={preview.onPointerEnter}
          onPointerLeave={preview.onPointerLeave}
          onPointerMove={preview.onPointerMove}
          overlay={<ThumbnailOverlay preview={preview} watched={row.kind === 'video' && row.watched} />}
        />
      ) : (
        <Thumbnail src={null} />
      )}
      <span className={styles.position} aria-hidden="true">
        {row.position}
      </span>
    </span>
  );

  const text = (
    <span className={styles.text}>
      <span className={cx(styles.cardTitle, gone && styles.goneTitle)}>
        <span className={styles.srOnly}>{row.position}. </span>
        {entryTitle ?? words.entryGone}
      </span>
      {row.kind === 'video' ? (
        row.author === null ? null : (
          <span className={styles.cardAuthor}>{row.author}</span>
        )
      ) : (
        <span className={styles.cardAuthor}>{words.entryGoneDetail}</span>
      )}
    </span>
  );

  // Manage mode: one <label>, no links, the whole card toggles — always, not only mid-selection.
  if (manage && row.indexId !== null) {
    return (
      <li className={cx(styles.card, row.selected && styles.cardSelected)} onFocus={onFocus}>
        {/* The shifted press is ours (2026-08-02) — `listing/selectPress.ts` measures why. */}
        <label className={styles.selectWrap} onClick={shiftRangePress((range) => onToggle(row.indexId ?? '', range))}>
          {select}
          {frame}
          {text}
        </label>
      </li>
    );
  }

  /*
   * A dead entry is never a link: the id is real, so an anchor would look live and land on "Video
   * not found". The row is kept — the numbering depends on it — and in manage mode it still selects.
   */
  if (gone) {
    return (
      <li
        className={cx(styles.card, styles.gone)}
        tabIndex={manage || cornerActions !== null ? undefined : tabIndex}
        onFocus={onFocus}
      >
        {select}
        {frame}
        {text}
        {cornerActions}
      </li>
    );
  }

  return (
    <li className={cx(styles.card, row.watched && styles.watchedCard)} onFocus={onFocus}>
      {select}
      <Link className={styles.thumbLink} href={row.href} tabIndex={-1} ariaHidden onClick={onPress}>
        {frame}
      </Link>
      <span className={styles.text}>
        <Link
          className={styles.cardTitle}
          href={row.href}
          title={entryTitle ?? row.title}
          tabIndex={tabIndex}
          onClick={onPress}
        >
          {/* The position is `aria-hidden` on the chip and read out here instead, so a screen reader
              gets "14. Under Pressure" as one label rather than a bare number beside it. */}
          <span className={styles.srOnly}>{row.position}. </span>
          {entryTitle ?? row.title}
        </Link>
        {row.author === null ? null : row.authorHref === null ? (
          <span className={styles.cardAuthor}>{row.author}</span>
        ) : (
          <Link className={styles.cardAuthor} href={row.authorHref} tabIndex={tabIndex}>
            {row.author}
          </Link>
        )}
      </span>
      {cornerActions}
      {/*
       * The two answers a press offers while a queue is running, over this entry's picture and only
       * while this is the card that was pressed (`queue/QueueCardAsk.tsx`). Only on this branch: a
       * dead entry is not a link, and in manage mode the whole card is its checkbox's label.
       */}
      <QueueCardAsk item={queueItem} tabIndex={tabIndex} />
    </li>
  );
}

/** The history page's glyph, redrawn locally — `ui/icons.tsx` keeps only the shared set. */
function RemoveGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 12h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function Paging({
  binding,
  state,
}: {
  readonly binding: PlaylistBinding;
  readonly state: Extract<PlaylistBinding['state'], { kind: 'ready' }>;
}) {
  const words = useCopy().text.playlist.page;
  // Suspended while a search is set — the observer would otherwise chase matches through the whole
  // playlist the moment a query empties the visible page. The button stays for a deliberate press.
  const autoLoad = useAutoLoadMore(
    state.loadMore === 'available' && binding.search.trim() === '',
    binding.loadMore,
  );

  return (
    // A finished list simply ends — the "that is all N" sentence is retired.
    <div className={styles.paging} ref={autoLoad}>
      {state.loadMore === null ? null : (
        <Button onClick={binding.loadMore} disabled={state.loadMore === 'loading'}>
          {state.loadMore === 'loading' ? words.loadingMore : words.loadMore(state.loaded)}
        </Button>
      )}
    </div>
  );
}
